<div class="modal-overlay" *ngIf="isOpen" (click)="closeModal()">
    <div class="modal-container" (click)="$event.stopPropagation()">
        <!-- Modal Header -->
        <div class="modal-header">
            <h3>
                <mat-icon>sort</mat-icon>
                Advanced Sorting & Search
            </h3>
            <button class="btn-close" (click)="closeModal()" aria-label="Close" type="button">
                <mat-icon>close</mat-icon>
            </button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
            <!-- Focused Column Indicator -->
            <div class="focused-column-banner" *ngIf="focusedColumn">
                <div class="banner-content">
                    <mat-icon>my_location</mat-icon>
                    <span class="banner-text">
                        Focusing on: <strong>{{ focusedColumn.displayName }}</strong>
                    </span>
                    <button class="btn-unfocus" (click)="clearFocus()" title="Search all columns" type="button">
                        <mat-icon>close</mat-icon> Search All Columns
                    </button>
                </div>
            </div>

            <!-- Search Box Section -->
            <div class="search-section">
                <h4><mat-icon>search</mat-icon> Search</h4>
                <div class="search-box">
                    <input 
                        type="text" 
                        [(ngModel)]="searchTerm" 
                        (ngModelChange)="onSearchChange()"
                        [placeholder]="focusedColumn ? 'Search in ' + focusedColumn.displayName + '...' : 'Search across all columns...'" 
                        class="search-input"
                    />
                    <button 
                        mat-icon-button
                        class="btn-clear" 
                        (click)="clearSearch()"
                        *ngIf="searchTerm"
                        aria-label="Clear search"
                        title="Clear search"
                        type="button">
                        <mat-icon>cancel</mat-icon>
                    </button>
                </div>
                <div class="search-options">
                    <label class="checkbox-label">
                        <input type="checkbox" [(ngModel)]="caseSensitive" (change)="onSearchChange()">
                        <span>Case sensitive</span>
                    </label>
                    <label class="checkbox-label">
                        <input type="checkbox" [(ngModel)]="exactMatch" (change)="onSearchChange()">
                        <span>Exact match</span>
                    </label>
                </div>
                <div class="search-hint" *ngIf="searchTerm">
                    <mat-icon>info</mat-icon>
                    <span *ngIf="focusedColumn">
                        Searching in: <strong>{{ focusedColumn.displayName }}</strong>
                    </span>
                    <span *ngIf="!focusedColumn">
                        Searching in: <strong>{{ getSearchFieldsText() }}</strong>
                    </span>
                </div>
            </div>

            <div class="section-divider"></div>

            <!-- Add New Sorting Rule Section -->
            <div class="add-rule-section">
                <h4><mat-icon>add</mat-icon> Add Sorting Rule</h4>
                <div class="add-rule-form">
                    <select [(ngModel)]="selectedColumnForSort" class="native-select">
                        <option value="">Select column...</option>
                        <option *ngFor="let col of availableColumnsForAdd" 
                                [value]="col.name"
                                [selected]="focusedColumn && col.name === focusedColumn.name">
                            {{ col.displayName }}
                            <span *ngIf="focusedColumn && col.name === focusedColumn.name"> (Focused)</span>
                        </option>
                    </select>

                    <select [(ngModel)]="selectedOrder" class="native-select">
                        <option value="asc">? Ascending (A-Z)</option>
                        <option value="desc">? Descending (Z-A)</option>
                    </select>

                    <button mat-raised-button color="primary"
                            (click)="addRule()" 
                            [disabled]="!selectedColumnForSort"
                            type="button">
                        <mat-icon>add</mat-icon> Add
                    </button>
                </div>
            </div>

            <!-- Current Rules Section -->
            <div class="rules-section">
                <div class="rules-header">
                    <h4>Sorting Rules ({{ sortRules.length }})</h4>
                    <button mat-stroked-button *ngIf="sortRules.length > 0" (click)="clearAll()" type="button">
                        <mat-icon>delete</mat-icon> Clear All
                    </button>
                </div>

                <div class="rules-hint" *ngIf="sortRules.length > 0">
                    <mat-icon>info</mat-icon>
                    Drag rules to change priority. Lower priority (top) is applied first.
                </div>

                <!-- Empty State -->
                <div class="empty-state" *ngIf="sortRules.length === 0">
                    <mat-icon style="font-size:48px; width:48px; height:48px;">sort</mat-icon>
                    <p>No sorting rules defined</p>
                    <small>Add a rule above to start sorting</small>
                </div>

                <!-- Rules List -->
                <div class="rules-list" 
                     cdkDropList
                     (cdkDropListDropped)="onDrop($event)"
                     *ngIf="sortRules.length > 0">
                    
                    <div *ngFor="let rule of sortRules; let i = index"
                         class="rule-item"
                         [class.disabled]="!rule.enabled"
                         cdkDrag>
                        
                        <!-- Drag Handle -->
                        <div class="drag-handle" cdkDragHandle>
                            <button mat-icon-button aria-label="Drag handle" type="button">
                                <mat-icon>drag_indicator</mat-icon>
                            </button>
                        </div>

                        <!-- Priority Badge -->
                        <div class="priority-badge">
                            {{ i + 1 }}
                        </div>

                        <!-- Rule Info -->
                        <div class="rule-info">
                            <div class="rule-column">
                                {{ rule.displayName }}
                            </div>
                            <div class="rule-order">
                                <select [(ngModel)]="rule.order"
                                        (change)="updateOrder(rule.id, rule.order)"
                                        class="native-select"
                                        [disabled]="!rule.enabled">
                                    <option value="asc">? Ascending</option>
                                    <option value="desc">? Descending</option>
                                </select>
                            </div>
                        </div>

                        <!-- Actions -->
                        <div class="rule-actions">
                            <!-- Move Up/Down -->
                            <button mat-icon-button (click)="moveUp(i)" [disabled]="i === 0" title="Move up" type="button">
                                <mat-icon>keyboard_arrow_up</mat-icon>
                            </button>
                            <button mat-icon-button (click)="moveDown(i)" [disabled]="i === sortRules.length - 1" title="Move down" type="button">
                                <mat-icon>keyboard_arrow_down</mat-icon>
                            </button>

                            <!-- Enable/Disable -->
                            <button mat-icon-button (click)="toggleRule(rule.id)" [class.active]="rule.enabled" title="{{ rule.enabled ? 'Disable' : 'Enable' }}" type="button">
                                <mat-icon *ngIf="rule.enabled">visibility</mat-icon>
                                <mat-icon *ngIf="!rule.enabled">visibility_off</mat-icon>
                            </button>

                            <!-- Remove -->
                            <button mat-icon-button color="warn" (click)="removeRule(rule.id)" title="Remove" type="button">
                                <mat-icon>delete</mat-icon>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="section-divider"></div>

            <!-- Filters Section -->
            <div class="filters-section">
                <div class="filters-header">
                    <h4><mat-icon>filter_list</mat-icon> Filters ({{ filterRules.length }})</h4>
                    <button mat-stroked-button *ngIf="filterRules.length > 0" (click)="clearAllFilters()" type="button">
                        <mat-icon>delete</mat-icon> Clear All Filters
                    </button>
                </div>

                <!-- Add Filter Control -->
                <div class="add-filter-form">
                    <!-- Debug info for empty dropdown -->
                    <div *ngIf="availableColumnsForFilter.length === 0 && filterRules.length === 0" 
                         class="filter-debug-info" 
                         style="padding: 12px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 4px; margin-bottom: 12px;">
                        <strong><mat-icon>warning</mat-icon> No Filterable Columns Available</strong>
                        <p style="margin: 8px 0 0 0; font-size: 13px;">
                            Please configure filterable columns in your component.
                        </p>
                    </div>
                    
                    <select 
                        class="native-select filter-column-select"
                        [(ngModel)]="selectedColumnForFilter"
                        (change)="onFilterColumnChange()">
                        <option value="">Select column to filter...</option>
                        <option *ngFor="let col of availableColumnsForFilter" 
                                [value]="col.name">
                            {{ col.displayName }} ({{ col.filterType }})
                        </option>
                    </select>

                    <!-- Boolean Filter -->
                    <div class="filter-value-container" *ngIf="selectedColumnForFilter && getColumnFilterType(selectedColumnForFilter) === 'boolean'">
                        <select 
                            class="native-select filter-value-select"
                            [(ngModel)]="filterValue">
                            <option [ngValue]="null">Select value...</option>
                            <option [ngValue]="true">? True (Active)</option>
                            <option [ngValue]="false">? False (Inactive)</option>
                        </select>
                    </div>

                    <!-- Text Filter -->
                    <div class="filter-value-container" *ngIf="selectedColumnForFilter && getColumnFilterType(selectedColumnForFilter) === 'text'">
                        <input 
                            type="text"
                            class="filter-value-input"
                            [(ngModel)]="filterValue"
                            placeholder="Enter value...">
                    </div>

                    <!-- Number Filter -->
                    <div class="filter-value-container" *ngIf="selectedColumnForFilter && getColumnFilterType(selectedColumnForFilter) === 'number'">
                        <input 
                            type="number"
                            class="filter-value-input"
                            [(ngModel)]="filterValue"
                            placeholder="Enter number...">
                    </div>

                    <!-- Dropdown Filter � options loaded from config data via configKey -->
                    <div class="filter-value-container" *ngIf="selectedColumnForFilter && getColumnFilterType(selectedColumnForFilter) === 'dropdown'">
                        <select class="native-select filter-value-select" [(ngModel)]="filterValue">
                            <option [ngValue]="null">Select value...</option>
                            <option *ngFor="let opt of getDropdownOptions(selectedColumnForFilter)"
                                    [ngValue]="opt.value">
                                {{ opt.label }}
                            </option>
                        </select>
                    </div>

                    <!-- Date Filter -->
                    <div class="filter-value-container" *ngIf="selectedColumnForFilter && getColumnFilterType(selectedColumnForFilter) === 'date'">
                        <input 
                            type="date"
                            class="filter-value-input"
                            [(ngModel)]="filterValue">
                    </div>

                    <!-- Date Range Filter -->
                    <div class="filter-date-range" *ngIf="selectedColumnForFilter && getColumnFilterType(selectedColumnForFilter) === 'dateRange'">
                        <div class="date-range-inputs">
                            <div class="date-input-group">
                                <label>From:</label>
                                <input 
                                    type="date"
                                    class="filter-value-input"
                                    [(ngModel)]="filterDateFrom">
                            </div>
                            <div class="date-input-group">
                                <label>To:</label>
                                <input 
                                    type="date"
                                    class="filter-value-input"
                                    [(ngModel)]="filterDateTo">
                            </div>
                        </div>
                    </div>

                    <button mat-raised-button color="primary" 
                        class="add-filter-btn"
                        (click)="addFilterRule()"
                        type="button"
                        [disabled]="!selectedColumnForFilter || (getColumnFilterType(selectedColumnForFilter) !== 'dateRange' && (filterValue === null || filterValue === '')) || (getColumnFilterType(selectedColumnForFilter) === 'dateRange' && (!filterDateFrom || !filterDateTo))">
                        <mat-icon>add</mat-icon> Add Filter
                    </button>
                </div>

                <!-- Active Filters List -->
                <div class="filters-list" *ngIf="filterRules.length > 0">
                    <div class="filter-rule" 
                         *ngFor="let rule of filterRules"
                         [class.disabled]="!rule.enabled">
                        <div class="filter-info">
                            <span class="filter-column">{{ rule.displayName }}</span>
                            <span class="filter-operator">=</span>
                            <span class="filter-value" 
                                  [class.boolean-true]="rule.filterType === 'boolean' && rule.value === true" 
                                  [class.boolean-false]="rule.filterType === 'boolean' && rule.value === false"
                                  [class.date-range]="rule.filterType === 'dateRange'">
                                <ng-container *ngIf="rule.filterType === 'boolean'">
                                    <mat-icon *ngIf="rule.value">check</mat-icon>
                                    <mat-icon *ngIf="!rule.value">close</mat-icon>
                                </ng-container>
                                {{ getFilterValueDisplay(rule) }}
                            </span>
                        </div>
                        
                        <div class="filter-actions">
                            <button mat-icon-button (click)="toggleFilterRule(rule.id)" [title]="rule.enabled ? 'Disable filter' : 'Enable filter'" type="button">
                                <mat-icon *ngIf="rule.enabled">visibility</mat-icon>
                                <mat-icon *ngIf="!rule.enabled">visibility_off</mat-icon>
                            </button>
                            <button mat-icon-button color="warn" (click)="removeFilterRule(rule.id)" title="Remove filter" type="button">
                                <mat-icon>delete</mat-icon>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div class="empty-state" *ngIf="filterRules.length === 0">
                    <mat-icon style="font-size:32px; width:32px; height:32px;">filter_list</mat-icon>
                    <p>No filters applied</p>
                    <small>Add filters to narrow down your results</small>
                </div>
            </div>

            <!-- Sort Summary -->
            <div class="sort-summary" *ngIf="sortRules.length > 0 || searchTerm || filterRules.length > 0">
                <strong>Active Settings:</strong>
                <div class="summary-text">
                    <span *ngIf="searchTerm">
                        <mat-icon>search</mat-icon> Search: "{{ searchTerm }}"
                        <span *ngIf="caseSensitive"> (case-sensitive)</span>
                        <span *ngIf="exactMatch"> (exact)</span>
                    </span>
                    <span *ngIf="filterRules.length > 0">
                        <span *ngIf="searchTerm"> | </span>
                        <mat-icon>filter_list</mat-icon> {{ filterRules.length }} filter(s)
                    </span>
                    <span *ngIf="sortRules.length > 0">
                        <span *ngIf="searchTerm || filterRules.length > 0"> | </span>
                        <mat-icon>sort</mat-icon> {{ sortRules.length }} sort rule(s)
                    </span>
                </div>
                <button mat-stroked-button class="btn-reset-summary" (click)="resetToDefaults()" title="Reset all search, filter and sort settings" type="button">
                    <mat-icon>refresh</mat-icon> Reset All
                </button>
            </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
            <button mat-stroked-button (click)="closeModal()" type="button">Cancel</button>
            <button mat-raised-button color="primary" (click)="apply()" type="button">
                <mat-icon>check</mat-icon> Apply
            </button>
        </div>
    </div>
</div>
